import { supabase } from '../supabase/client';
import { addTimelineEvent } from './timeline';
import { createNotification } from '../notifications/createNotification';
import { zielUserFuerTimeline } from './zustaendigkeit';
import { WorkflowState } from './types';

export const getWorkflowState = (rechnung: {
  brutto: number;
  status: string;
  freigegeben?: boolean;
  tech_pruefung_status?: string | null;
}): WorkflowState => {
  const brauchtFreigabe = rechnung.brutto >= 3000;
  const istFreigegeben = rechnung.freigegeben === true;
  const techPruefungLaeuft = rechnung.tech_pruefung_status === 'angefordert';
  const hatTechPruefung = rechnung.tech_pruefung_status != null;
  const kannBezahlen =
    rechnung.status !== 'bezahlt' &&
    !techPruefungLaeuft &&
    (!brauchtFreigabe || istFreigegeben);

  return { kannBezahlen, brauchtFreigabe, istFreigegeben, hatTechPruefung, techPruefungLaeuft };
};

export const requestTechPruefung = async (
  rechnungId: string,
  vonUserId: string,
  anUserId: string,
  anUserName: string
): Promise<void> => {
  const { error } = await supabase
    .from('rechnungen')
    .update({
      tech_pruefung_von: vonUserId,
      tech_pruefung_an: anUserId,
      tech_pruefung_status: 'angefordert',
      technisch_geprueft_von: null,
      technisch_geprueft_am: null,
    })
    .eq('id', rechnungId);
  if (error) throw error;

  await addTimelineEvent(
    rechnungId,
    'tech_pruefung_angefordert',
    vonUserId,
    `Zur technischen Prüfung an ${anUserName} gesendet`,
    anUserId
  );

  await createNotification({
    benutzerId: anUserId,
    modul: 'rechnungen',
    typ: 'tech_pruefung_angefordert',
    nachricht: `Technische Prüfung angefordert für eine Rechnung`,
    referenzId: rechnungId,
    referenzTyp: 'rechnung',
    linkPfad: '/admin/rechnungen',
    erstelltVon: vonUserId,
  });
};

export const respondTechPruefung = async (
  rechnungId: string,
  prueferId: string,
  ergebnis: 'freigegeben' | 'abgelehnt',
  kommentar?: string
): Promise<void> => {
  // Anforderer-ID lesen, bevor sie genullt wird
  const { data: rechnung } = await supabase
    .from('rechnungen')
    .select('tech_pruefung_von')
    .eq('id', rechnungId)
    .single();

  const updateData: Record<string, unknown> = {
    tech_pruefung_status: ergebnis,
    technisch_geprueft_von: prueferId,
    technisch_geprueft_am: new Date().toISOString(),
    tech_pruefung_an: null,
  };

  const { error } = await supabase
    .from('rechnungen')
    .update(updateData)
    .eq('id', rechnungId);
  if (error) throw error;

  const label = ergebnis === 'freigegeben' ? 'Technisch freigegeben' : 'Technisch abgelehnt';
  const inhalt = kommentar ? `${label}: ${kommentar}` : label;
  await addTimelineEvent(rechnungId, 'tech_pruefung_ergebnis', prueferId, inhalt);

  if (rechnung?.tech_pruefung_von) {
    await createNotification({
      benutzerId: rechnung.tech_pruefung_von,
      modul: 'rechnungen',
      typ: 'tech_pruefung_ergebnis',
      nachricht: `Technische Prüfung: ${label}`,
      referenzId: rechnungId,
      referenzTyp: 'rechnung',
      linkPfad: '/admin/rechnungen',
      erstelltVon: prueferId,
    });
  }
};

export const requestFreigabe = async (
  rechnungId: string,
  vonUserId: string,
  anUserId: string,
  anUserName: string
): Promise<void> => {
  const { error } = await supabase
    .from('rechnungen')
    .update({
      freigabe_angefordert_von: vonUserId,
      freigabe_angefordert_an: anUserId,
      freigegeben: false,
      freigegeben_von: null,
      freigegeben_am: null,
    })
    .eq('id', rechnungId);
  if (error) throw error;

  await addTimelineEvent(
    rechnungId,
    'freigabe_angefordert',
    vonUserId,
    `Vier-Augen-Freigabe angefordert — an ${anUserName} (Pflicht: Betrag ≥ 3.000 €)`,
    anUserId
  );

  await createNotification({
    benutzerId: anUserId,
    modul: 'rechnungen',
    typ: 'freigabe_angefordert',
    nachricht: `Vier-Augen-Freigabe angefordert für eine Rechnung (≥ 3.000 €)`,
    referenzId: rechnungId,
    referenzTyp: 'rechnung',
    linkPfad: '/admin/rechnungen',
    erstelltVon: vonUserId,
  });
};

export const respondFreigabe = async (
  rechnungId: string,
  freigaberId: string,
  ergebnis: 'freigegeben' | 'abgelehnt',
  kommentar?: string
): Promise<void> => {
  // Anforderer-ID lesen, bevor sie genullt wird
  const { data: rechnungFreigabe } = await supabase
    .from('rechnungen')
    .select('freigabe_angefordert_von')
    .eq('id', rechnungId)
    .single();

  const updateData: Record<string, unknown> = {
    freigabe_angefordert_an: null,
  };

  if (ergebnis === 'freigegeben') {
    updateData.freigegeben = true;
    updateData.freigegeben_von = freigaberId;
    updateData.freigegeben_am = new Date().toISOString();
  }

  const { error } = await supabase
    .from('rechnungen')
    .update(updateData)
    .eq('id', rechnungId);
  if (error) throw error;

  const label = ergebnis === 'freigegeben' ? 'Vier-Augen-Freigabe erteilt' : 'Vier-Augen-Freigabe abgelehnt';
  const inhalt = kommentar ? `${label}: ${kommentar}` : label;
  await addTimelineEvent(rechnungId, 'freigabe_ergebnis', freigaberId, inhalt);

  if (rechnungFreigabe?.freigabe_angefordert_von) {
    await createNotification({
      benutzerId: rechnungFreigabe.freigabe_angefordert_von,
      modul: 'rechnungen',
      typ: 'freigabe_ergebnis',
      nachricht: label,
      referenzId: rechnungId,
      referenzTyp: 'rechnung',
      linkPfad: '/admin/rechnungen',
      erstelltVon: freigaberId,
    });
  }
};

export const markAsBezahlt = async (
  rechnungId: string,
  userId: string,
  brutto: number,
  freigegeben: boolean,
  userRole?: string
): Promise<void> => {
  // Admins und Super Admins umgehen das Vier-Augen-Prinzip.
  const role = userRole?.toLowerCase();
  const istAdmin = role === 'admin' || role === 'super admin';
  if (brutto >= 3000 && !freigegeben && !istAdmin) {
    throw new Error('Vier-Augen-Freigabe erforderlich für Rechnungen ab 3.000 €');
  }

  const { error } = await supabase
    .from('rechnungen')
    .update({
      status: 'bezahlt',
      bezahlt_von: userId,
      bezahlt_timestamp: new Date().toISOString(),
      // klaerung_wiedervorlage_am nullen (Spec 2026-07-28) — bezahlt ist nie klaerung.
      klaerung_wiedervorlage_am: null,
    })
    .eq('id', rechnungId);
  if (error) throw error;

  await addTimelineEvent(rechnungId, 'bezahlung', userId, 'Als bezahlt markiert');

  // Zuständigen benachrichtigen (falls vorhanden und nicht der gleiche User)
  const { data: rechnungBezahlt } = await supabase
    .from('rechnungen')
    .select('zustaendig_id')
    .eq('id', rechnungId)
    .single();

  if (rechnungBezahlt?.zustaendig_id && rechnungBezahlt.zustaendig_id !== userId) {
    await createNotification({
      benutzerId: rechnungBezahlt.zustaendig_id,
      modul: 'rechnungen',
      typ: 'bezahlung',
      nachricht: 'Eine Rechnung wurde als bezahlt markiert',
      referenzId: rechnungId,
      referenzTyp: 'rechnung',
      linkPfad: '/admin/rechnungen',
      erstelltVon: userId,
    });
  }
};

export const changeZuweisung = async (
  rechnungId: string,
  userId: string,
  neuerZustaendigId: string | null,
  neuerZustaendigName: string,
  neuerZustaendigTyp: 'mitarbeiter' | 'team' | null = null
): Promise<void> => {
  const { error } = await supabase
    .from('rechnungen')
    .update({ zustaendig_id: neuerZustaendigId, zustaendig_typ: neuerZustaendigTyp })
    .eq('id', rechnungId);
  if (error) throw error;

  const inhalt = neuerZustaendigId
    ? `Zugewiesen an ${neuerZustaendigName}`
    : 'Zuweisung entfernt (Global)';
  // NICHT neuerZustaendigId durchreichen: die ist polymorph (Person ODER Team),
  // ziel_user_id hat aber einen FK auf profiles. Eine Team-Id lehnte Postgres ab —
  // und weil der Wurf erst NACH dem Update oben kommt, blieb die Rechnung halb
  // gespeichert zurück (Ticket Björn 09.09.2026).
  await addTimelineEvent(
    rechnungId, 'zuweisung', userId, inhalt,
    zielUserFuerTimeline(neuerZustaendigId, neuerZustaendigTyp) || undefined,
  );

  // Persönliche Benachrichtigung nur an eine Person — Teams haben keinen benutzerId-Empfänger.
  if (neuerZustaendigTyp !== 'team' && neuerZustaendigId && neuerZustaendigId !== userId) {
    await createNotification({
      benutzerId: neuerZustaendigId,
      modul: 'rechnungen',
      typ: 'zuweisung',
      nachricht: 'Eine Rechnung wurde Ihnen zugewiesen',
      referenzId: rechnungId,
      referenzTyp: 'rechnung',
      linkPfad: '/admin/rechnungen',
      erstelltVon: userId,
    });
  }
};
